import { useState } from "react";
import { listApiKeys, createApiKey, revokeApiKey } from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type ApiKey = {
  id: string;
  orgId: string;
  keyPrefix: string;
  name: string;
  permissions: string[];
  status: string;
  lastUsedAt: string | null;
  createdAt: string;
};

export function AdminApiKeysPage() {
  const { token } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [form, setForm] = useState({ name: "", createdBy: "", permissions: "read" });
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!orgId) return;
    const res = (await listApiKeys(token, orgId)) as ApiKey[];
    setKeys(res ?? []);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setLoading(true);
    setNewSecret(null);
    try {
      const res = (await createApiKey(token, {
        orgId,
        name: form.name,
        createdBy: form.createdBy,
        permissions: form.permissions.split(",").map((p) => p.trim()),
      })) as ApiKey & { secret: string };
      setNewSecret(res.secret);
      await load();
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!window.confirm("Revoke this API key? This action cannot be undone.")) return;
    await revokeApiKey(token, id, form.createdBy);
    await load();
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">API Keys</h1>

        <div className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="flex gap-3">
            <input
              className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Organization ID"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            />
            <button
              onClick={load}
              className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
            >
              Load
            </button>
          </div>

          <form onSubmit={handleCreate} className="flex flex-wrap gap-3">
            <input
              className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Key name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <input
              className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Created by user ID"
              value={form.createdBy}
              onChange={(e) => setForm((p) => ({ ...p, createdBy: e.target.value }))}
              required
            />
            <input
              className="min-w-32 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Permissions (comma-separated)"
              value={form.permissions}
              onChange={(e) => setForm((p) => ({ ...p, permissions: e.target.value }))}
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Create Key
            </button>
          </form>

          {newSecret && (
            <div className="rounded border border-yellow-700 bg-yellow-900 p-3 text-sm">
              <div className="mb-1 font-semibold text-yellow-200">
                Secret (shown once only — copy now):
              </div>
              <code className="break-all text-yellow-100">{newSecret}</code>
            </div>
          )}
        </div>

        {keys.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="pb-2">Name</th>
                  <th className="pb-2">Prefix</th>
                  <th className="pb-2">Permissions</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Last Used</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-gray-800 last:border-0">
                    <td className="py-2 font-medium text-white">{k.name}</td>
                    <td className="py-2 font-mono text-xs text-indigo-400">{k.keyPrefix}...</td>
                    <td className="py-2 text-xs">{k.permissions.join(", ")}</td>
                    <td className="py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${k.status === "ACTIVE" ? "bg-green-900 text-green-200" : "bg-red-900 text-red-200"}`}
                      >
                        {k.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-gray-500">
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}
                    </td>
                    <td className="py-2">
                      {k.status === "ACTIVE" && (
                        <button
                          onClick={() => handleRevoke(k.id)}
                          className="text-xs text-red-400 hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
